'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useVisibilityAwarePolling } from '@/hooks/useVisibilityAwarePolling';
import { resolveApiUrl } from '@/lib/utils/api-auth';
import {
  CommentNotification,
  fetchMyNotifications,
  markAllNotificationsRead as apiMarkAllRead,
  markNotificationRead as apiMarkRead,
} from '@/lib/api/notifications';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NotificationContextValue {
  /** Total unread count: pending invites (org + project) plus unread comment notifications. */
  unreadCount: number;
  /** True briefly after unreadCount increases — drives pulse animation. */
  hasNewNotification: boolean;
  /** All comment notifications returned by the last fetch. */
  commentNotifications: CommentNotification[];
  /** Number of unread comment notifications as reported by the API. */
  unreadCommentCount: number;
  /** Mark a single comment notification as read by id. */
  markNotificationRead: (id: string) => Promise<void>;
  /** Mark all comment notifications as read. */
  markAllNotificationsRead: () => Promise<void>;
  /** Manually trigger a re-fetch (e.g. after accepting/declining an invite). */
  refresh: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

// Baseline polling cadence when SSE never comes online — matches pre-SSE
// behaviour.
const POLL_INTERVAL_MS = 30_000;
// Relaxed polling cadence while SSE IS online — a periodic safety net that
// also refreshes the comment list (SSE only pushes the unread-count scalar).
const SSE_ACTIVE_POLL_INTERVAL_MS = 120_000;
// After this many failed reconnect attempts in a row, give up on SSE and go
// back to polling for the rest of the session.
const SSE_MAX_CONSECUTIVE_FAILURES = 3;

type SSEEvent = { type: 'count'; count: number } | { type: 'ping'; timestamp?: string };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
// All notification-context state mutations flow through a reducer so each
// transition is a single, named action. The previous design split these
// across six independent useState hooks plus a logout-reset effect that
// fired three setters synchronously — the React Compiler correctly flagged
// that as a derived-state cascade. Folding the related pieces into one
// reducer turns "user logged out" into a single `RESET_FOR_LOGOUT`
// dispatch, which the rule treats as a real transition rather than a
// synchronization of derived state.

type NotificationState = {
  unreadCount: number;
  hasNewNotification: boolean;
  commentNotifications: CommentNotification[];
  unreadCommentCount: number;
  sseActive: boolean;
  sseGaveUp: boolean;
};

type NotificationAction =
  | { type: 'APPLY_COUNT'; total: number; previous: number }
  | { type: 'PULSE_OFF' }
  | {
      type: 'FETCH_SUCCESS';
      commentNotifications: CommentNotification[];
      unreadCommentCount: number;
      total: number;
      previous: number;
    }
  | { type: 'MARK_ONE_READ'; id: string }
  | { type: 'MARK_ALL_READ' }
  | { type: 'SSE_OPENED' }
  | { type: 'SSE_CLOSED' }
  | { type: 'SSE_GAVE_UP' }
  | { type: 'RESET_FOR_LOGOUT' };

const initialNotificationState: NotificationState = {
  unreadCount: 0,
  hasNewNotification: false,
  commentNotifications: [],
  unreadCommentCount: 0,
  sseActive: false,
  sseGaveUp: false,
};

function notificationReducer(state: NotificationState, action: NotificationAction): NotificationState {
  switch (action.type) {
    case 'APPLY_COUNT': {
      const rising = action.total > action.previous;
      return {
        ...state,
        unreadCount: action.total,
        hasNewNotification: rising || state.hasNewNotification,
      };
    }
    case 'PULSE_OFF':
      if (!state.hasNewNotification) return state;
      return { ...state, hasNewNotification: false };
    case 'FETCH_SUCCESS': {
      const rising = action.total > action.previous;
      return {
        ...state,
        commentNotifications: action.commentNotifications,
        unreadCommentCount: action.unreadCommentCount,
        unreadCount: action.total,
        hasNewNotification: rising || state.hasNewNotification,
      };
    }
    case 'MARK_ONE_READ': {
      // Only decrement the counters when the targeted notification is
      // currently UNREAD. Without this guard, clicking an already-read row
      // (the rows stay clickable regardless of read state — see
      // NotificationPanel's CommentNotificationItem) would still subtract 1
      // from both counters, silently dropping the badge while genuinely
      // unread comments remain. The id may also not be present (a stale row
      // already pruned by a poll), in which case nothing is decremented.
      const target = state.commentNotifications.find((n) => n.id === action.id);
      const wasUnread = !!target && !target.read;
      return {
        ...state,
        commentNotifications: state.commentNotifications.map((n) => (n.id === action.id ? { ...n, read: true } : n)),
        unreadCommentCount: wasUnread ? Math.max(0, state.unreadCommentCount - 1) : state.unreadCommentCount,
        unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      };
    }
    case 'MARK_ALL_READ':
      return {
        ...state,
        commentNotifications: state.commentNotifications.map((n) => ({ ...n, read: true })),
        unreadCount: Math.max(0, state.unreadCount - state.unreadCommentCount),
        unreadCommentCount: 0,
      };
    case 'SSE_OPENED':
      if (state.sseActive) return state;
      return { ...state, sseActive: true };
    case 'SSE_CLOSED':
      if (!state.sseActive) return state;
      return { ...state, sseActive: false };
    case 'SSE_GAVE_UP':
      if (state.sseGaveUp) return state;
      return { ...state, sseGaveUp: true, sseActive: false };
    case 'RESET_FOR_LOGOUT':
      return initialNotificationState;
    // Defensive: the switch is exhaustive over the closed action union, so the
    // default arm is unreachable (kept for forward-compat type-safety).
    /* v8 ignore next 2 */
    default:
      return state;
  }
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [state, dispatch] = useReducer(notificationReducer, initialNotificationState);
  const { unreadCount, hasNewNotification, commentNotifications, unreadCommentCount, sseActive, sseGaveUp } = state;

  const prevCountRef = useRef(0);
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);

  // Applies a new unread-count and schedules the pulse-off timer. Used by
  // both the SSE and polling paths so behaviour is uniform.
  const applyUnreadCount = useCallback((total: number) => {
    if (!mountedRef.current) return;
    const previous = prevCountRef.current;
    dispatch({ type: 'APPLY_COUNT', total, previous });
    if (total > previous) {
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      pulseTimerRef.current = setTimeout(() => {
        if (mountedRef.current) dispatch({ type: 'PULSE_OFF' });
      }, 2000);
    }
    prevCountRef.current = total;
  }, []);

  const fetchCount = useCallback(async () => {
    if (!mountedRef.current) return;
    try {
      // `silent: true` reshapes the timeout error into an AbortError so the
      // catch below can recognise the recoverable race where the Go API's
      // IdleTimeout closes a kept-alive connection right as our poll lands
      // on it. Without this the user would see a "Request timed out after
      // 120000ms" red-error log every ~2 minutes of idle.
      const data = await fetchMyNotifications({ silent: true });
      const inviteCount = data.orgInvites.length + data.projectInvites.length;
      const commentUnread = data.unreadCommentCount ?? 0;
      const total = inviteCount + commentUnread;

      const previous = prevCountRef.current;
      dispatch({
        type: 'FETCH_SUCCESS',
        commentNotifications: data.commentNotifications ?? [],
        unreadCommentCount: commentUnread,
        total,
        previous,
      });
      if (total > previous) {
        if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
        pulseTimerRef.current = setTimeout(() => {
          if (mountedRef.current) dispatch({ type: 'PULSE_OFF' });
        }, 2000);
      }
      prevCountRef.current = total;
    } catch (err) {
      // Silently swallow the recoverable idle-close race (see `silent: true`
      // above). Either the AbortError name OR the timed-out message variant
      // qualifies — the next poll will succeed. Other errors fall through to
      // the same silent path the previous bare-catch had: per the existing
      // contract above, network errors don't disrupt the UI.
      const e = err as { name?: string; message?: string } | undefined;
      const isAbort = e?.name === 'AbortError';
      const isTimeout = typeof e?.message === 'string' && e.message.includes('timed out after');
      if (isAbort || isTimeout) {
        // Recoverable — do NOT log, do NOT bump any visible counter.
        return;
      }
      // Other errors: keep prior behaviour (silent UI).
    }
  }, []);

  const markNotificationRead = useCallback(async (id: string) => {
    await apiMarkRead(id);
    dispatch({ type: 'MARK_ONE_READ', id });
  }, []);

  const markAllNotificationsRead = useCallback(async () => {
    await apiMarkAllRead();
    dispatch({ type: 'MARK_ALL_READ' });
  }, []);

  const refresh = useCallback(async () => {
    await fetchCount();
  }, [fetchCount]);

  // Track mounted state and reset counters on logout. The reset is a
  // single `RESET_FOR_LOGOUT` dispatch, replacing the three synchronous
  // setState calls that triggered set-state-in-effect.
  useEffect(() => {
    mountedRef.current = true;

    if (!user) {
      dispatch({ type: 'RESET_FOR_LOGOUT' });
      prevCountRef.current = 0;
    }

    return () => {
      mountedRef.current = false;
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    };
  }, [user]);

  // ── SSE subscription ─────────────────────────────────────────────────────
  // Opens a long-lived stream whenever the user is authenticated. Count
  // events update the badge immediately; comment-notification metadata still
  // comes from the periodic poll (which runs at a relaxed cadence while SSE
  // is active). On repeated reconnect failures we give up and let the poll
  // at its normal cadence cover for us.
  useEffect(() => {
    if (!user) return;
    if (sseGaveUp) return;
    if (typeof window === 'undefined') return;
    if (typeof EventSource === 'undefined') return;

    let eventSource: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;
    // Exponential backoff (1s, 2s, 4s, capped at 10s).
    let backoffMs = 1000;
    let consecutiveFailures = 0;

    const resolveStreamUrl = () => resolveApiUrl('/api/me/notifications/stream');

    const scheduleReconnect = () => {
      if (disposed) return;
      consecutiveFailures += 1;
      if (consecutiveFailures >= SSE_MAX_CONSECUTIVE_FAILURES) {
        dispatch({ type: 'SSE_GAVE_UP' });
        return;
      }
      if (reconnectTimer) clearTimeout(reconnectTimer);
      const delay = Math.min(backoffMs, 10_000);
      backoffMs = Math.min(backoffMs * 2, 10_000);
      reconnectTimer = setTimeout(connect, delay);
    };

    function connect() {
      if (disposed) return;
      try {
        const streamUrl = resolveStreamUrl();
        let source: EventSource;
        try {
          const parsed = new URL(streamUrl, window.location.href);
          if (parsed.origin !== window.location.origin) {
            source = new EventSource(parsed.toString(), { withCredentials: true });
          } else {
            source = new EventSource(parsed.toString());
          }
        } catch {
          source = new EventSource(streamUrl);
        }
        eventSource = source;

        source.onopen = () => {
          if (disposed) return;
          consecutiveFailures = 0;
          backoffMs = 1000;
          dispatch({ type: 'SSE_OPENED' });
        };

        source.onmessage = (evt) => {
          if (disposed || !evt.data) return;
          try {
            const parsed = JSON.parse(evt.data) as SSEEvent;
            if (parsed.type === 'count' && typeof parsed.count === 'number') {
              applyUnreadCount(parsed.count);
            }
            // Ping is only a heartbeat; no action required.
          } catch {
            // Ignore malformed frames — the server guarantees JSON, but a
            // stray proxy could inject noise and we'd rather skip than crash.
          }
        };

        source.onerror = () => {
          if (disposed) return;
          dispatch({ type: 'SSE_CLOSED' });
          source.close();
          eventSource = null;
          scheduleReconnect();
        };
      } catch {
        scheduleReconnect();
      }
    }

    connect();

    return () => {
      disposed = true;
      dispatch({ type: 'SSE_CLOSED' });
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
    };
  }, [user, sseGaveUp, applyUnreadCount]);

  // ── Polling fallback ─────────────────────────────────────────────────────
  // Always runs: it's how comment-notification metadata stays fresh, and it's
  // also the sole source of truth whenever SSE is down. The interval relaxes
  // to 2 minutes while SSE is delivering pushes so we're not hammering the
  // API for something the stream already covers.
  const pollIntervalMs = sseActive && !sseGaveUp ? SSE_ACTIVE_POLL_INTERVAL_MS : POLL_INTERVAL_MS;
  useVisibilityAwarePolling(fetchCount, {
    intervalMs: pollIntervalMs,
    enabled: !!user,
  });

  const value = useMemo<NotificationContextValue>(
    () => ({
      unreadCount,
      hasNewNotification,
      commentNotifications,
      unreadCommentCount,
      markNotificationRead,
      markAllNotificationsRead,
      refresh,
    }),
    [
      unreadCount,
      hasNewNotification,
      commentNotifications,
      unreadCommentCount,
      markNotificationRead,
      markAllNotificationsRead,
      refresh,
    ],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (ctx === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return ctx;
}
